// The Forms v2 engine: screens with multiple fields per screen, in-place
// visible_when reveal, multi-field rows via layout_row/layout_width,
// start/end screens picked by variant targeting, {{var:}}/{{context:}}
// interpolation, review and progress. Navigation is computed from position
// and visibility — there are no logic jumps in v2.
//
// Saving: text inputs save on blur, discrete inputs on change, and only
// when the value actually differs from what the server holds (a snapshot
// tracks that). Continue flushes whatever is pending — so reload returns
// the user to where they left off with their answers intact, and no screen
// change fires a burst of per-field requests.

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm, type UseFormReturn } from "react-hook-form";
import equal from "fast-deep-equal";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronRightIcon,
  DocumentTextIcon,
  HeartIcon,
  InformationCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

import { useAnalytics } from "analytics/AnalyticsContext";
import ProgressBar from "components/Form/ProgressBar";
import Button from "components/Button";
import Spinner from "components/Spinner";
import { substituteVariables } from "components/Form/variables";
import { FieldConfig } from "components/Form/types";
import { NetworkError } from "hooks/usePublicAPI";

import { gatewayClient, type FormV2Client } from "./api";
import { evaluateCondition, ConditionScope } from "./conditions";
import type { FormV2Event, FormV2EventInput, FormV2ScreenKind } from "./events";
import { FormReadOnlyContext } from "./read-only-context";
import V2Field from "./fields";
import { RenderableField } from "./fields/types";
import ReviewList from "./ReviewList";
import { V2ResolvedField, V2ResolvedScreen, V2ResolvedView } from "./types";

// The house button, as v1 wears it — peach with soil text.
// The one box every button family shares — border, padding, radius, gap —
// so the same label is the same size whether the button is the primary or
// the outline. Only colors differ between families; the primary's border
// simply matches its fill. Exported so the quote flow builds its outline
// on it rather than on a drifting copy.
export const BUTTON_BOX_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg border-2 px-[26px] py-2.5 font-bold transition-all disabled:pointer-events-none disabled:opacity-50 aria-busy:opacity-100";

// The keyboard focus ring for controls on the flow's light surfaces (page,
// white, oat, ivory): evergreen clears WCAG 2.2's 3:1 non-text contrast on
// all of them. Not for controls on dark fills, where it would vanish.
export const FOCUS_RING_CLASS =
  "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-evergreen";

// Exported so a harness's own forward buttons (the quote screen's
// Continue) wear exactly this, not a drifting copy.
export const CONTINUE_BUTTON_CLASS = `${BUTTON_BOX_CLASS} border-cantelope bg-cantelope text-soil hover:border-soil hover:bg-soil hover:text-cantelope ${FOCUS_RING_CLASS}`;

const BACK_LINK_CLASS =
  "font-bold text-soil underline underline-offset-4 transition-colors hover:text-soil/70";

// The footnote icons the authoring catalog offers, by name. An absent or
// unknown name renders no icon — never a guessed one.
// Kept in sync by hand with FORM_FOOTNOTE_ICONS in after-internal
// src/server/modules/forms/domain/models/form-screen-variant.ts.
const FOOTNOTE_ICONS: Record<string, typeof ShieldCheckIcon> = {
  shield: ShieldCheckIcon,
  info: InformationCircleIcon,
  lock: LockClosedIcon,
  document: DocumentTextIcon,
  heart: HeartIcon,
};

// Composite fields lay out their own internals; they always claim a full
// row no matter what layout_row says.
const FULL_ROW_TYPES = new Set([
  "full_name",
  "location",
  "long_text",
  "section_heading",
]);

type Step =
  | { kind: "start" }
  | {
      kind: "body";
      index: number;
      returnToReview?: boolean;
      /** Present when the harness directed the walk here — both edges
       * hand control back instead of stepping. */
      directed?: FormV2DirectedEntry;
    }
  | { kind: "review" }
  // External review only: everything outside the held screens is done and
  // the walk stands by — the harness heard onBodyComplete and owns what
  // happens next (its juncture, its review, its directed re-entries).
  | { kind: "body_complete" }
  // Everything answerable is answered, and the application still cannot
  // finish — a completion requirement (a plan not yet chosen) is unmet.
  // The server owns that verdict; this is how the renderer presents it.
  | { kind: "context_missing" }
  | { kind: "end" };

/** Where the walk stands, position by ref — the synchronous mirror of
 * `step` that a queued control anchors off before React commits a move. */
interface WalkPosition {
  kind: Step["kind"];
  /** The body screen's ref; null off body steps. */
  screenRef: string | null;
  returnToReview?: boolean;
}

/** Where the walker stands, as the hooks see it. */
export interface FormV2StepRef {
  kind: Step["kind"];
  /** The screen's ref, on body steps. */
  screenRef?: string;
}

export type FormV2Transition = "advance" | "back" | "edit" | "submit";

/**
 * External review: the harness owns the review moment and the completion.
 * The definition's own review step never renders, the natural walk never
 * advances into a held screen, and nothing auto-completes — when
 * everything outside the held screens is done, the walk settles and
 * `onBodyComplete` fires with the resolved screens so the harness can
 * build its review without a duplicate fetch. Held screens are reached
 * only by `goToScreen`, normally with a directed entry.
 */
export interface FormV2ExternalReview {
  /** Refs of the body screens the natural walk must never enter (the
   * SSN screen the harness saves for after its review). */
  heldScreens: readonly string[];
}

/**
 * A harness-directed stay on one screen. Continue validates and flushes
 * the screen, then either hands control back (`onContinue` — the held
 * SSN screen, where the harness completes behind it) or settles the walk
 * back on body_complete (`continueTo` — the edit-from-review round trip:
 * onBodyComplete re-fires with the fresh screens and values, which is
 * exactly what re-runs the harness's juncture). Back (offered only when
 * `onBack` is given) hands straight back, blur-time saves having fired
 * as on any Back press.
 */
export type FormV2DirectedEntry = {
  onBack?: () => void;
  /** What the forward button says; "Continue" when omitted. */
  continueLabel?: string;
} & (
  | { onContinue: () => void; continueTo?: undefined }
  | { continueTo: "body_complete"; onContinue?: undefined }
);

/**
 * The seams a harness embedding this renderer plugs into — a quote flow
 * that re-prices when answers change, and holds a step transition while it
 * does. All optional; the standalone renderer passes none and behaves as
 * before.
 *
 * `onFieldSaved` fires after the server acknowledged the save — the safe
 * moment to prefetch against the new answers. `onBeforeStepChange` fires
 * with every save already flushed; returning "hold" keeps the customer on
 * the current step, and the harness owns whatever UI explains why.
 */
export interface FormV2Hooks {
  onFieldChange?: (args: { fieldRef: string; value: unknown }) => void;
  onFieldSaved?: (args: {
    fieldRef: string;
    value: unknown;
    /** The visible body screen holding the field at save time; null when
     * no visible screen serves it. */
    screenRef: string | null;
    required: boolean;
    /** Every required visible field on that screen holds a complete saved
     * answer — the checkpoint prefetch's firing condition, computed here
     * so the harness needs no field→screen map of its own. */
    screenRequiredComplete: boolean;
  }) => void;
  onBeforeStepChange?: (args: {
    from: FormV2StepRef;
    to: FormV2StepRef;
    kind: FormV2Transition;
    /** The answers as the server now holds them, by field ref. */
    answers: Record<string, unknown>;
  }) => "proceed" | "hold" | Promise<"proceed" | "hold">;
  /**
   * A completion the server acknowledged — the outcome is frozen. Fires
   * exactly once per acknowledged completion, after the walk has settled
   * on its end step, so the harness may unmount the renderer from here. A
   * refused completion (review sends the customer back, or a completion
   * requirement is unmet) never fires it, and neither does loading a
   * response that was already complete.
   */
  onCompleted?: (args: { responseId: string }) => void;
  /**
   * The first body screen's Back link. The renderer shows one there only
   * when this hook is present, and pressing it calls the hook instead of
   * stepping — the harness owns whatever stands before the form (its
   * quote screen). Leaving the form is not a walk transition, so
   * `onBeforeStepChange` never hears it; blur-time saves fire as they
   * would on any Back press.
   */
  onBackFromFirstScreen?: () => void;
  /**
   * External review only: everything outside the held screens is done —
   * on the validated Continue off the last walk screen, and again on any
   * later arrival back at that state (a reload landing there, an edit
   * round trip finishing). Fires each time the walk settles there, never
   * merely on re-render. `screens` are the visible walk screens (held
   * ones excluded) as resolved, `values` the answers the server holds —
   * enough to build a review with no duplicate fetch.
   */
  onBodyComplete?: (args: {
    screens: V2ResolvedScreen[];
    values: Record<string, unknown>;
    /** The last walk screen's ref — where Back-from-review re-enters. */
    lastScreenRef: string | null;
  }) => void;
  /**
   * Every analytics-worthy moment in the walk — a screen shown, an answer
   * saved, validation refused — as a vendor-free signal. The renderer never
   * calls a vendor itself; the harness forwards these to whatever it
   * tracks with. Optional, and cost-free when absent.
   */
  onEvent?: (event: FormV2Event) => void;
}

/**
 * The imperative seam a harness drives the walk through, exposed on the
 * component ref. Both controls flush the current screen's pending saves
 * first — the same flush a step transition runs — but neither consults
 * `onBeforeStepChange`: the caller holding the controls is the gatekeeper,
 * and gating a jump the gatekeeper commanded invites deadlock. Controls
 * run one at a time — a second call queues behind the first and anchors
 * off wherever it landed. A control invoked while a held transition is
 * still pending should be paired with a "hold" verdict, or the resolving
 * transition lands on top of it.
 */
export interface FormV2Controls {
  /**
   * Stand on the body screen bearing this ref, if the fresh visible screen
   * list still serves it. `ok: false` — with no navigation — when the ref
   * is unknown or hidden, and when the flush itself failed (the connection
   * banner shows and the answers stay pending). `currentScreenRef` names
   * the body screen the walk stands on afterwards, null when it stands on
   * a start/review/end step instead. Screen validation deliberately does
   * not run: the harness is routing the customer elsewhere, not asserting
   * this screen finished. `directed` makes the stay harness-owned — both
   * edges hand control back (held screens are only ever entered this
   * way, or with a plain jump the harness supervises).
   */
  goToScreen(
    screenRef: string,
    opts?: { directed?: FormV2DirectedEntry },
  ): Promise<{ ok: boolean; currentScreenRef: string | null }>;
  /**
   * Re-read the resolved view and adopt it wholesale — the review-entry
   * reconcile — then re-anchor the current screen by ref, falling back the
   * way a vanished screen falls back on advance: forward, never to the
   * start screen mid-walk. For a harness detour that changed resolution
   * context underneath the form (a re-selection settling a new variant).
   * Under external review, a walk standing on body_complete re-places:
   * new unanswered screens put the customer back in the walk, otherwise
   * it settles on body_complete again and onBodyComplete re-fires with
   * the fresh state. Resolves with where the walk now stands. Rejects
   * when the flush or the re-read fails — nothing is adopted and the walk
   * stays put — except the vanished-response 404, which the built-in
   * reload recovery absorbs.
   */
  refresh(): Promise<{ standing: FormV2StepRef }>;
  /**
   * Complete the response from wherever the walk stands — the Submit of a
   * harness that owns review and completion. Flushes the standing screen's
   * pending saves like every control and consults no gate (the caller is
   * the gatekeeper). Emits `form_submitted` as the request leaves and
   * `form_completed` the moment the server acknowledges — before
   * resolving, so a harness that unmounts the renderer on `ok` still saw
   * it. A refusal (4xx) emits `form_completion_refused`, re-reads the view
   * so the refused state is on screen without moving, and resolves
   * `ok: false` with the status for the caller's own recovery. Anything
   * else — no response loaded, a failed flush, a fault — rejects.
   */
  complete(): Promise<{ ok: true } | { ok: false; status: number }>;
}

/**
 * The link's grant token addresses the default gateway client; a harness
 * that authenticates its own way passes `client` instead, and no token
 * exists. Exactly one of the two.
 *
 * `skipStartScreen` — initial placement never lands on the start screen:
 * a harness that already welcomed the customer drops the walk straight
 * onto the first thing to answer. The standalone page omits it and keeps
 * its start screen.
 */
type FormV2Props = {
  formRef: string;
  hooks?: FormV2Hooks;
  skipStartScreen?: boolean;
  /**
   * Renders the walk as a record, not a form: every field disabled,
   * nothing saved, no walk footer — the host owns whatever actions make
   * sense around it. Placement lands on the first walk screen (a
   * completed response otherwise lands on its end screen), and the host
   * moves between screens through `goToScreen`. Fixed for the mount:
   * a host flipping modes remounts (key the element).
   */
  readOnly?: boolean;
  /** Shown while the walk loads, in place of the bare spinner. The host
   * names the wait so a reload reads as one continuous moment; the
   * renderer owns how it looks. */
  loadingScreen?: {
    section?: string;
    /** Plain text, or a fragment when the title carries styled spans. */
    title: React.ReactNode;
    description?: string;
  };
  /** Present = the harness owns review and completion; see the type. Must
   * be referentially stable or memoized — a new identity re-derives the
   * walk shape. */
  externalReview?: FormV2ExternalReview;
} & (
  | { grantToken: string; client?: undefined }
  | { client: FormV2Client; grantToken?: undefined }
);

// A double-render racing itself would resume twice and could create two
// responses — share the in-flight call per client. The default client also
// dedupes across instances of the same link (see api.ts); a custom client
// owns that layer itself.
const resumesInFlight = new WeakMap<
  FormV2Client,
  Promise<{ id: string; created: boolean }>
>();

function resumeOnce(
  client: FormV2Client,
): Promise<{ id: string; created: boolean }> {
  const existing = resumesInFlight.get(client);
  if (existing) return existing;
  const inFlight = client
    .resume()
    .then((resumed) => ({
      id: resumed.form_response.id,
      created: resumed.created,
    }))
    .finally(() => {
      resumesInFlight.delete(client);
    });
  resumesInFlight.set(client, inFlight);
  return inFlight;
}

/** The server said no to a completion for a reason the customer can fix —
 * required answers outstanding, a stale answer, an unmet requirement — as
 * opposed to a vanished response (404, which recovery owns) or a fault. */
function isRefusal(error: unknown): error is NetworkError {
  return (
    error instanceof NetworkError &&
    error.statusCode >= 400 &&
    error.statusCode < 500 &&
    error.statusCode !== 404
  );
}

function FormV2(props: FormV2Props, ref: React.ForwardedRef<FormV2Controls>) {
  const {
    formRef,
    hooks,
    skipStartScreen,
    readOnly = false,
    externalReview,
    loadingScreen,
  } = props;
  const { track: trackAnalytics } = useAnalytics();
  // The walk shape under external review: which screens the natural walk
  // skips, and that review/auto-complete are the harness's. Derived once
  // per prop identity — the prop must be stable.
  const walkShape = useMemo<WalkShape>(
    () => ({
      held: new Set(externalReview?.heldScreens ?? []),
      external: externalReview !== undefined,
    }),
    [externalReview],
  );
  // One client for the whole walk — a new client identity restarts it, so
  // a custom client must be referentially stable across renders.
  const apiClient = useMemo(
    () =>
      props.client !== undefined
        ? props.client
        : gatewayClient(formRef, props.grantToken),
    [props.client, formRef, props.grantToken],
  );
  const [view, setView] = useState<V2ResolvedView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [step, setStep] = useState<Step>({ kind: "start" });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Answers as of the last screen advance — what {{field:}} interpolation
  // reads, so copy follows what was entered without shifting under the
  // customer as they type.
  const [committedValues, setCommittedValues] = useState<
    Record<string, unknown>
  >({});

  const methods = useForm({ mode: "onTouched" });
  const { getValues, setValue, trigger, reset, watch } = methods;
  const formData = watch();

  // What the server already holds, by field ref — the dirty check that
  // keeps saves down to actual changes. Cloned so later typing can't
  // mutate the snapshot through a shared object reference.
  const savedValuesRef = useRef<Record<string, unknown>>({});
  // Per-field save chains: a second commit for the same field waits for
  // the first, so PUTs for one answer can never race each other.
  const saveChainsRef = useRef<Record<string, Promise<boolean>>>({});
  const responseIdRef = useRef<string | null>(null);
  const loadedResponseRef = useRef<string | null>(null);
  // Fields whose template default has been applied this session — a
  // default is offered once, never re-imposed on a value the customer
  // clears.
  const defaultedRef = useRef<Set<string>>(new Set());
  // The harness's hooks, read through a ref so the save/navigation
  // callbacks stay referentially stable when the embedding page re-renders.
  const hooksRef = useRef<FormV2Hooks | undefined>(hooks);
  hooksRef.current = hooks;
  // The freshest server view, synchronously — setView lands next commit,
  // but a save's ack can reshape the screens and the very next navigation
  // decision must walk the reshaped list, not a stale closure.
  const viewRef = useRef<V2ResolvedView | null>(null);
  // The same idea for the walk position: kept in lockstep with `step` at
  // render (below the screen memos) and written synchronously by the
  // imperative controls, so a queued control anchors off where the
  // previous one just moved instead of a stale closure.
  const positionRef = useRef<WalkPosition>({ kind: "start", screenRef: null });

  // Every FormV2Event names the form and the live response, so call sites
  // only ever supply what's unique to that moment. Each one goes to the
  // app's analytics fan-out (PostHog + signals) under its generic name,
  // and to the harness's onEvent so it can mirror it under its own scope.
  // The identity is null only before anything resolved (a failed load).
  const emitEvent = useCallback(
    (event: FormV2EventInput) => {
      const stamped: FormV2Event = {
        ...event,
        form_id: viewRef.current?.form_response.form_definition_id ?? null,
        form_ref: viewRef.current?.definition.ref ?? formRef,
        form_response_id: responseIdRef.current,
      };
      const { type, ...properties } = stamped;
      trackAnalytics(type, properties);
      hooksRef.current?.onEvent?.(stamped);
    },
    [formRef, trackAnalytics],
  );

  // Adopt a freshly-read view wholesale: the server's answers replace the
  // form state, so an answer newly marked stale drops out and re-asks —
  // exactly what a reload would do. Callers only do this at points where
  // every edit has been flushed.
  const ingestView = useCallback(
    (resolved: V2ResolvedView) => {
      const values = answerValues(resolved);
      savedValuesRef.current = cloneValue(values) as Record<string, unknown>;
      viewRef.current = resolved;
      setView(resolved);
      reset(values);
      setCommittedValues(cloneValue(values) as Record<string, unknown>);
    },
    [reset],
  );

  // Adopt a view mid-screen, from a save's ack or a conflict: screens and
  // progress refresh, but the customer keeps whatever they are typing —
  // only untouched fields adopt server values. Interpolation's committed
  // snapshot deliberately stays put until the next screen change.
  const reconcileView = useCallback(
    (resolved: V2ResolvedView) => {
      const values = answerValues(resolved);
      savedValuesRef.current = cloneValue(values) as Record<string, unknown>;
      viewRef.current = resolved;
      setView(resolved);
      reset(values, { keepDirtyValues: true });
    },
    [reset],
  );

  /** Resume and seed the walk from scratch — the mount load, and the
   * recovery when the response vanished underneath this session. */
  const loadFromServer = useCallback(async () => {
    const resumed = await resumeOnce(apiClient);
    const resolved = await apiClient.fetchResolvedView(resumed.id);
    responseIdRef.current = resolved.form_response.id;
    defaultedRef.current = new Set();
    setProblem(null);
    ingestView(resolved);
    const values = answerValues(resolved);
    // Once per response: a StrictMode double effect (or a recovery reload
    // of the same response) re-runs this load without the form having
    // been loaded again in any sense the analytics care about. "Resumed"
    // is the server's word — a response that existed before this visit —
    // not "has answers", which a prefilled form always does.
    if (loadedResponseRef.current === resolved.form_response.id) {
      // A duplicate load of the response already on screen — StrictMode's
      // double mount, or a transient-404 recovery that resumed the same
      // response. The data refresh above is welcome; the PLACEMENT is
      // not: a control (the URL drive) may already have moved the walk,
      // and re-placing would stomp it.
      return;
    }
    loadedResponseRef.current = resolved.form_response.id;
    emitEvent({
      type: "form_loaded",
      resumed: !resumed.created,
      screen_count: visibleBodyScreensOf(resolved, values).length,
    });
    const landing = initialStep(resolved, values, walkShape);
    if (readOnly && landing.kind === "end") {
      // A record opens on its first page, not on the thank-you screen a
      // live walk ends on.
      setStep(stepPastStart(resolved, values, walkShape));
      return;
    }
    setStep(
      skipStartScreen && landing.kind === "start"
        ? stepPastStart(resolved, values, walkShape)
        : landing,
    );
  }, [apiClient, ingestView, skipStartScreen, readOnly, walkShape, emitEvent]);

  // The client is the whole identity of this page — for the default
  // client, that is the link.
  // The one place a load gives up: the not-found face, and the event that
  // says so — with the status when the network supplied one.
  const failLoad = useCallback(
    (error: unknown) => {
      emitEvent({
        type: "form_load_failed",
        status: error instanceof NetworkError ? error.statusCode : null,
      });
      setLoadFailed(true);
    },
    [emitEvent],
  );

  useEffect(() => {
    let mounted = true;
    loadFromServer().catch((error: unknown) => {
      if (mounted) failLoad(error);
    });
    return () => {
      mounted = false;
    };
  }, [loadFromServer, failLoad]);

  // A 404 from a response-scoped call means the response vanished
  // underneath this session — deleted, superseded, or the grant revoked.
  // Answers sent at it go nowhere, so stop the walk and re-resume: a live
  // grant lands on a fresh response, a dead one on the not-found screen.
  // One recovery at a time — a screen's burst of 404s reloads once.
  const reloadingRef = useRef(false);
  const recoverIfGone = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof NetworkError) || error.statusCode !== 404) {
        return false;
      }
      if (reloadingRef.current) return true;
      reloadingRef.current = true;
      // The loading state, immediately — no more typing into a void. The
      // vanished response is no longer "the" response either: anything
      // emitted before the reload lands (a form_load_failed, say) names
      // none rather than the stale id.
      viewRef.current = null;
      responseIdRef.current = null;
      setView(null);
      setProblem(null);
      loadFromServer()
        .catch(failLoad)
        .finally(() => {
          reloadingRef.current = false;
        });
      return true;
    },
    [loadFromServer, failLoad],
  );

  // Every step transition lands at the top of the page, instantly — on a
  // phone, the next screen otherwise opens scrolled to wherever the last
  // one ended. Reveals-in-place are not steps and never jump.
  const bodyIndex = step.kind === "body" ? step.index : null;
  useEffect(() => {
    snapToTop();
  }, [step.kind, bodyIndex]);

  // Every field edit, as it happens — the harness cancels a stale prefetch
  // on this signal. Composite parts report under their field's root ref.
  useEffect(() => {
    const subscription = watch((_, info) => {
      const onFieldChange = hooksRef.current?.onFieldChange;
      if (!info.name || !onFieldChange) return;
      const fieldRef = info.name.split(".")[0];
      onFieldChange({ fieldRef, value: getValues(fieldRef) });
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues]);

  // Entering review re-reads the resolved view. An in-flight response's
  // context is re-derived on every read, so this is where a mid-fill change
  // to the underlying record (a corrected market, an updated policy)
  // surfaces — as re-resolved wording, and as answers newly marked stale.
  useEffect(() => {
    if (step.kind !== "review") return;
    const responseId = responseIdRef.current;
    if (!responseId) return;
    let cancelled = false;
    apiClient
      .fetchResolvedView(responseId)
      .then((refreshed) => {
        if (!cancelled) ingestView(refreshed);
      })
      .catch((error: unknown) => {
        if (!cancelled) recoverIfGone(error);
        // Otherwise the review still renders from the view already loaded.
      });
    return () => {
      cancelled = true;
    };
  }, [step.kind, apiClient, ingestView, recoverIfGone]);

  // A completion the server acknowledged and the harness has not heard
  // about yet. The hook fires from an effect so the walk has settled on the
  // end step first — the harness typically unmounts the renderer in the
  // callback. Loading an already-completed response lands on "end" without
  // ever setting this, so it reports nothing.
  const completedAckRef = useRef<string | null>(null);
  useEffect(() => {
    if (step.kind !== "end") return;
    const responseId = completedAckRef.current;
    if (responseId === null) return;
    completedAckRef.current = null;
    hooksRef.current?.onCompleted?.({ responseId });
  }, [step.kind]);

  // The server's ack, the moment it lands: the analytics event fires HERE,
  // synchronously, not from the end-step effect above — a harness driving
  // `controls.complete()` unmounts the renderer as soon as the promise
  // resolves, and an effect on an unmounted component never runs.
  const acknowledgeCompletion = useCallback(
    (responseId: string) => {
      emitEvent({ type: "form_completed" });
      completedAckRef.current = responseId;
    },
    [emitEvent],
  );

  // The external-review handoff: each time the walk settles on
  // body_complete (never on a mere re-render there), the harness hears it
  // with the resolved walk screens in hand. Keyed on the step OBJECT —
  // every setStep is a fresh identity, so an edit round trip or a refresh
  // settling here again re-fires with the fresh screens and values, which
  // is what re-runs the harness's juncture. A plain re-render never does.
  useEffect(() => {
    if (step.kind !== "body_complete") return;
    const latest = viewRef.current;
    if (!latest) return;
    const screens = walkScreensOf(latest, getValues(), walkShape.held);
    hooksRef.current?.onBodyComplete?.({
      screens,
      values: cloneValue(savedValuesRef.current) as Record<string, unknown>,
      lastScreenRef: screens[screens.length - 1]?.ref ?? null,
    });
  }, [step, getValues, walkShape]);

  const scope: ConditionScope = useMemo(
    () => ({
      answers: formData,
      context: view?.form_response.resolved_context ?? {},
      variables: view?.variables ?? {},
    }),
    [formData, view],
  );

  const interpolate = useCallback(
    (input: string) =>
      substituteVariables(input, {
        variables: view?.variables ?? {},
        context: view?.form_response.resolved_context ?? {},
        answers: committedValues,
      }),
    [view, committedValues],
  );

  const screenVisible = useCallback(
    (screen: V2ResolvedScreen) =>
      screen.visible_when === null ||
      evaluateCondition(screen.visible_when, scope),
    [scope],
  );
  const fieldVisible = useCallback(
    (field: V2ResolvedField) =>
      field.variant.visible_when === null ||
      evaluateCondition(field.variant.visible_when, scope),
    [scope],
  );

  const startScreen = useMemo(
    () =>
      view?.screens.find(
        (screen) => screen.kind === "start" && screenVisible(screen),
      ) ?? null,
    [view, screenVisible],
  );
  const bodyScreens = useMemo(
    () =>
      (view?.screens ?? []).filter(
        (screen) => screen.kind === "body" && screenVisible(screen),
      ),
    [view, screenVisible],
  );
  const endScreen = useMemo(
    () =>
      view?.screens.find(
        (screen) => screen.kind === "end" && screenVisible(screen),
      ) ?? null,
    [view, screenVisible],
  );

  // form_screen_viewed: exactly once per landed screen. A dedupe key (step
  // identity alone) rather than the effect's own deps guards against the
  // one real race here — the initial default step is frequently also where
  // the load lands once resolved (a fresh response opens on "start" both
  // before and after data arrives), so `view` rides in the deps to retry
  // that first screen once it exists; the key then absorbs every later
  // `view` update — an answer-triggered mid-screen reshape — without
  // re-firing for a screen already reported.
  const lastViewedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!view) return;
    const key = `${step.kind}:${bodyIndex}`;
    if (lastViewedKeyRef.current === key) return;
    lastViewedKeyRef.current = key;
    const kind = formScreenKind(step.kind);
    if (!kind) return; // body_complete / context_missing: nothing to report
    const screen = screenFor(
      kind,
      startScreen,
      bodyScreens,
      bodyIndex,
      endScreen,
    );
    emitEvent({
      type: "form_screen_viewed",
      screen_ref: screen?.ref ?? null,
      screen_kind: kind,
      screen_index: kind === "body" ? bodyIndex : null,
      screen_position: screen?.position ?? null,
      screen_count: bodyScreens.length,
    });
  }, [
    step.kind,
    bodyIndex,
    view,
    startScreen,
    bodyScreens,
    endScreen,
    emitEvent,
  ]);

  const allVisibleFields = useMemo(
    () => bodyScreens.flatMap((screen) => screen.fields.filter(fieldVisible)),
    [bodyScreens, fieldVisible],
  );

  // Any render after a setStep sees that step or a later one, so this
  // assignment can never roll the position back behind a control's own
  // synchronous write.
  positionRef.current = {
    kind: step.kind,
    screenRef:
      step.kind === "body" ? (bodyScreens[step.index]?.ref ?? null) : null,
    returnToReview: step.kind === "body" ? step.returnToReview : undefined,
  };

  // Template defaults — "Name of insured or annuitant" starting as the
  // insured's full name. Applied once, at the moment a field is on screen
  // (reveals included) with no answer saved and nothing typed; from then
  // on it is an ordinary editable answer, and a value the customer clears
  // stays cleared.
  useEffect(() => {
    // A record offers no defaults — nothing here is being answered.
    if (readOnly) return;
    if (step.kind !== "body") return;
    const screen = bodyScreens[step.index];
    if (!screen) return;
    for (const field of screen.fields.filter(fieldVisible)) {
      const template = field.variant.field_properties.default_value;
      if (!template || defaultedRef.current.has(field.ref)) continue;
      if (savedValuesRef.current[field.ref] !== undefined) continue;
      const current = getValues(field.ref);
      if (current !== undefined && current !== "") continue;
      const value = interpolate(template).replace(/\s+/g, " ").trim();
      defaultedRef.current.add(field.ref);
      if (!value) continue;
      setValue(field.ref, value);
    }
  });

  /** Save one field's current value if it changed; true = in sync. */
  const runSave = useCallback(
    async (fieldRef: string): Promise<boolean> => {
      // A record never writes — flushes trivially succeed, so the
      // imperative controls (goToScreen) work unchanged over one.
      if (readOnly) return true;
      const responseId = responseIdRef.current;
      if (!responseId) return true;
      // Frozen at read time: getValues hands back react-hook-form's LIVE
      // object, which keeps mutating as the customer types. The request
      // body and the snapshot must both be this moment's value, or a
      // keystroke landing mid-PUT gets absorbed into the snapshot and never
      // saved.
      const value = cloneValue(getValues(fieldRef));
      if (value === undefined) return true;
      if (equal(value, savedValuesRef.current[fieldRef])) return true;
      // The other half of the answer: the wording this client rendered.
      // Nothing served for the ref means the view is behind — leave the
      // value pending; a reconcile will land and the next flush retries.
      const field = servedField(viewRef.current, fieldRef);
      if (field === null) return false;
      try {
        const saved = await apiClient.saveAnswer(
          responseId,
          fieldRef,
          value,
          field.variant.id,
        );
        savedValuesRef.current[fieldRef] = value;
        // Adopted in arrival order: the server serializes resolves per
        // response, so a later-arriving ack reflects a later resolve. A
        // transport reorder is rare and self-heals at the next read.
        if (saved.view) reconcileView(saved.view);
        // Post-server-ack, so a harness prefetching against the new answers
        // never runs ahead of what the server holds. The screen context is
        // computed against the freshest view — a reshaping ack included —
        // so the checkpoint prefetch fires on true required-set state.
        const fieldContext = savedFieldContext(
          viewRef.current,
          getValues(),
          fieldRef,
          savedValuesRef.current,
        );
        hooksRef.current?.onFieldSaved?.({
          fieldRef,
          value: cloneValue(value),
          ...fieldContext,
        });
        // No answer value here — field refs and shape only.
        emitEvent({
          type: "form_answer_saved",
          screen_ref: fieldContext.screenRef,
          field_ref: fieldRef,
          field_type: field.type,
          required: fieldContext.required,
        });
        return true;
      } catch (error) {
        // Gone underneath us — recovery owns the screen from here.
        if (recoverIfGone(error)) return false;
        // The wording moved under us — adopt the truth the refusal carries.
        // The value stays local and unsaved, so the next flush re-commits
        // it against the wording now shown.
        if (error instanceof NetworkError && error.statusCode === 409) {
          const resolved = (error.responseData as { resolved?: V2ResolvedView })
            ?.resolved;
          if (resolved) reconcileView(resolved);
        }
        // Left un-snapshotted on purpose: the next commit or Continue
        // retries, and Continue surfaces the failure.
        return false;
      }
    },
    [apiClient, getValues, reconcileView, recoverIfGone, emitEvent, readOnly],
  );

  const commitField = useCallback(
    (fieldRef: string): Promise<boolean> => {
      const prior = saveChainsRef.current[fieldRef] ?? Promise.resolve(true);
      const next = prior.then(() => runSave(fieldRef));
      saveChainsRef.current[fieldRef] = next;
      return next;
    },
    [runSave],
  );

  /** Commit every given field (unchanged ones no-op) and await the lot. */
  const flushSaves = useCallback(
    async (fieldRefs: string[]): Promise<boolean> => {
      const results = await Promise.all(fieldRefs.map(commitField));
      return results.every(Boolean);
    },
    [commitField],
  );

  /** Where a step stands, as the hooks see it. */
  const stepRefOf = useCallback(
    (target: Step): FormV2StepRef => {
      if (target.kind === "body") {
        return { kind: "body", screenRef: bodyScreens[target.index]?.ref };
      }
      return { kind: target.kind };
    },
    [bodyScreens],
  );

  /** Runs the harness's transition gate; false = hold, stay put. Callers
   * fire it only after every edit in play has been flushed. When the flush
   * itself reshaped the screens, `nextScreens` names the target against
   * the fresh list — `from` still reads the list the customer stood on. */
  const gateStep = useCallback(
    async (
      next: Step,
      kind: FormV2Transition,
      nextScreens?: V2ResolvedScreen[],
    ): Promise<boolean> => {
      const gate = hooksRef.current?.onBeforeStepChange;
      if (!gate) return true;
      const decision = await gate({
        from: stepRefOf(step),
        to:
          nextScreens && next.kind === "body"
            ? { kind: "body", screenRef: nextScreens[next.index]?.ref }
            : stepRefOf(next),
        kind,
        answers: cloneValue(savedValuesRef.current) as Record<string, unknown>,
      });
      return decision !== "hold";
    },
    [step, stepRefOf],
  );

  const submit = useCallback(async () => {
    if (!view) return;
    setBusy(true);
    setProblem(null);
    try {
      const flushed = await flushSaves(
        allVisibleFields.map((field) => field.ref),
      );
      if (!flushed) {
        setProblem(
          "We couldn't save your answers — check your connection and try again.",
        );
        snapToTop();
        return;
      }
      setCommittedValues(
        cloneValue(savedValuesRef.current) as Record<string, unknown>,
      );
      // The harness's last word before anything irreversible: a held submit
      // stays where it is, and the harness owns the UI that says why.
      if (!(await gateStep({ kind: "end" }, "submit"))) return;
      emitEvent({ type: "form_submitted" });
      await apiClient.complete(view.form_response.id);
      // The outcome froze on that line — whatever the confirmatory read
      // does next, the harness hears about the completion once the walk
      // lands on its end step (the recovery reload's landing included).
      acknowledgeCompletion(view.form_response.id);
      const refreshed = await apiClient.fetchResolvedView(
        view.form_response.id,
      );
      viewRef.current = refreshed;
      setView(refreshed);
      setStep({ kind: "end" });
    } catch (error) {
      // Gone underneath us — recovery owns the screen from here.
      if (recoverIfGone(error)) return;
      if (isRefusal(error)) {
        emitEvent({
          type: "form_completion_refused",
          status: error.statusCode,
        });
      }
      // The server said no — requirements or required fields stand in the
      // way. It owns that truth; re-read it, and put what needs another
      // look in front of the customer on the review step, whether or not
      // this form normally shows one.
      try {
        const refreshed = await apiClient.fetchResolvedView(
          view.form_response.id,
        );
        ingestView(refreshed);
        if (refreshed.form_response.status === "completed") {
          // The completion itself stood — only the confirmatory read
          // failed, and this re-read just proved it. Land on the end step.
          setStep({ kind: "end" });
          return;
        }
        const { progress } = refreshed;
        if (
          progress.stale_field_refs.length > 0 ||
          progress.unanswered_required_field_refs.length > 0
        ) {
          setStep({ kind: "review" });
          setProblem(
            // A stale answer gets the banner on review; don't double up.
            progress.stale_field_refs.length > 0
              ? null
              : "A few answers are still needed before this can be submitted.",
          );
        } else if (!progress.completion_requirements_met) {
          // Nothing left to ask, and still not finishable: something outside
          // the form (a plan not yet chosen) has to settle first. An end
          // state, not an error.
          setStep({ kind: "context_missing" });
        } else {
          setProblem("This can't be submitted just yet — please try again.");
        }
      } catch {
        setProblem(
          "We couldn't save your answers — check your connection and try again.",
        );
      }
      snapToTop();
    } finally {
      setBusy(false);
    }
  }, [
    view,
    emitEvent,
    acknowledgeCompletion,
    apiClient,
    flushSaves,
    allVisibleFields,
    ingestView,
    gateStep,
    recoverIfGone,
  ]);

  const advance = useCallback(
    async (fromIndex: number, returnToReview: boolean | undefined) => {
      const screen = bodyScreens[fromIndex];
      if (!screen) return;
      const visibleFields = screen.fields.filter(fieldVisible);
      // Busy from the first await: async validators (the phone check, the
      // address unit check) and a harness holding the transition take real
      // time, and the button must say so.
      setBusy(true);
      try {
        const valid = await trigger(visibleFields.flatMap(validationNames));
        if (!valid) {
          const refused = invalidFieldRefs(visibleFields, methods);
          emitEvent({
            type: "form_validation_failed",
            screen_ref: screen.ref,
            field_refs: refused,
          });
          revealField(refused[0]);
          return;
        }
        const flushed = await flushSaves(
          visibleFields.map((field) => field.ref),
        );
        if (!flushed) {
          setProblem(
            "We couldn't save your answers — check your connection and try again.",
          );
          return;
        }
        setCommittedValues(
          cloneValue(savedValuesRef.current) as Record<string, unknown>,
        );
        setProblem(null);

        // The flush may have reshaped the screens — a library slot filling
        // in the moment the triggering answer landed. Walk the freshest
        // list, re-anchored by ref: the index the customer stood on means
        // nothing in a list that just changed.
        const latest = viewRef.current;
        const fresh = latest
          ? visibleBodyScreensOf(latest, getValues())
          : bodyScreens;
        const anchorIndex = fresh.findIndex(
          (candidate) => candidate.ref === screen.ref,
        );
        const showReview = latest
          ? latest.definition.show_review
          : view?.definition.show_review;

        let next: Step | null = null;
        if (returnToReview) {
          next = { kind: "review" };
        } else if (anchorIndex === -1 && latest) {
          // The screen just answered vanished with its own answers — land
          // where a reload would, except never backward onto the start
          // screen: mid-form, an emptied walk ends forward.
          const fallback = initialStep(latest, getValues(), walkShape);
          if (fallback.kind === "start") {
            next =
              showReview && !walkShape.external ? { kind: "review" } : null;
          } else {
            next = fallback;
          }
        } else {
          // The next screen the natural walk may enter — held screens are
          // the harness's to direct, never a Continue's landing.
          const onward = fresh.findIndex(
            (candidate, index) =>
              index > anchorIndex && !walkShape.held.has(candidate.ref),
          );
          if (onward !== -1) {
            next = { kind: "body", index: onward };
          } else if (walkShape.external) {
            next = { kind: "body_complete" };
          } else if (showReview) {
            next = { kind: "review" };
          }
        }
        if (next === null) {
          if (walkShape.external) {
            // Nothing left to walk, and completion is the harness's call.
            if (await gateStep({ kind: "body_complete" }, "advance", fresh)) {
              setStep({ kind: "body_complete" });
            }
            return;
          }
          await submit();
          return;
        }
        if (await gateStep(next, "advance", fresh)) {
          // next can also be body_complete here (the external-review walk
          // ran out of screens) — a harness handoff, not a screen, so
          // formScreenKind is null and nothing is emitted for it.
          const toKind = formScreenKind(next.kind);
          if (toKind) {
            emitEvent({
              type: "form_screen_advanced",
              from_screen_ref: screen.ref,
              from_screen_index: fromIndex,
              to_screen_ref:
                next.kind === "body" ? (fresh[next.index]?.ref ?? null) : null,
              to_screen_kind: toKind,
            });
          }
          setStep(next);
        }
      } finally {
        setBusy(false);
      }
    },
    [
      bodyScreens,
      fieldVisible,
      trigger,
      flushSaves,
      view,
      submit,
      gateStep,
      getValues,
      walkShape,
      emitEvent,
      methods,
    ],
  );

  const goBack = useCallback(
    async (fromIndex: number) => {
      // Step back over any held screen — the natural walk neither enters
      // nor re-enters what the harness holds.
      let index = fromIndex - 1;
      while (index >= 0 && walkShape.held.has(bodyScreens[index]?.ref ?? "")) {
        index -= 1;
      }
      if (index < 0) return;
      const next: Step = { kind: "body", index };
      setBusy(true);
      try {
        if (await gateStep(next, "back")) {
          emitEvent({
            type: "form_screen_back",
            from_screen_ref: bodyScreens[fromIndex]?.ref ?? null,
            from_screen_index: fromIndex,
            to_screen_ref: bodyScreens[index]?.ref ?? null,
            to_screen_kind: "body",
          });
          setStep(next);
        }
      } finally {
        setBusy(false);
      }
    },
    [gateStep, bodyScreens, walkShape, emitEvent],
  );

  const startForm = useCallback(async () => {
    const firstWalkIndex = bodyScreens.findIndex(
      (screen) => !walkShape.held.has(screen.ref),
    );
    if (firstWalkIndex === -1) {
      if (walkShape.external) {
        setStep({ kind: "body_complete" });
        return;
      }
      await submit();
      return;
    }
    const next: Step = { kind: "body", index: firstWalkIndex };
    setBusy(true);
    try {
      if (await gateStep(next, "advance")) setStep(next);
    } finally {
      setBusy(false);
    }
  }, [bodyScreens, submit, gateStep, walkShape]);

  const editFromReview = useCallback(
    async (screenIndex: number) => {
      const next: Step = {
        kind: "body",
        index: screenIndex,
        returnToReview: true,
      };
      if (await gateStep(next, "edit")) setStep(next);
    },
    [gateStep],
  );

  /** The directed stay's forward edge: validate and flush the screen the
   * customer stands on, then hand the walk back or settle it on
   * body_complete — no transition gate; the harness directing the stay is
   * the gatekeeper. */
  const directedContinue = useCallback(
    async (index: number, directed: FormV2DirectedEntry) => {
      const screen = bodyScreens[index];
      if (!screen) return;
      const visibleFields = screen.fields.filter(fieldVisible);
      setBusy(true);
      try {
        const valid = await trigger(visibleFields.flatMap(validationNames));
        if (!valid) {
          const refused = invalidFieldRefs(visibleFields, methods);
          emitEvent({
            type: "form_validation_failed",
            screen_ref: screen.ref,
            field_refs: refused,
          });
          revealField(refused[0]);
          return;
        }
        const flushed = await flushSaves(
          visibleFields.map((field) => field.ref),
        );
        if (!flushed) {
          setProblem(
            "We couldn't save your answers — check your connection and try again.",
          );
          return;
        }
        setCommittedValues(
          cloneValue(savedValuesRef.current) as Record<string, unknown>,
        );
        setProblem(null);
        // A directed Continue is still an advance out of a body screen —
        // the harness's transition gate (its eligibility checkpoints)
        // hears it exactly like the natural walk's. Held = stay put; the
        // directed entry stays armed for the retry.
        const proceed = await gateStep(
          { kind: "body_complete" } as Step,
          "advance",
        );
        if (!proceed) return;
        if (directed.onContinue) {
          // Control returns straight to the harness — no local screen
          // change, so nothing for form_screen_advanced to name here.
          directed.onContinue();
        } else {
          positionRef.current = { kind: "body_complete", screenRef: null };
          // No form_screen_advanced: body_complete is a harness handoff,
          // not a screen kind the analytics contract tracks (formScreenKind
          // is null for it) — the onBodyComplete/juncture events the host
          // fires next pick up the story.
          setStep({ kind: "body_complete" });
        }
      } finally {
        setBusy(false);
      }
    },
    [
      bodyScreens,
      fieldVisible,
      trigger,
      flushSaves,
      gateStep,
      emitEvent,
      methods,
    ],
  );

  // One control at a time: a second call queues behind the first and then
  // anchors off wherever it landed (positionRef), so two controls can
  // never undo each other's navigation.
  const controlChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const runControl = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    const run = controlChainRef.current.then(async () => {
      setBusy(true);
      try {
        return await task();
      } finally {
        setBusy(false);
      }
    });
    controlChainRef.current = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }, []);

  /** Flush the standing body screen's pending saves — the same flush a
   * step transition runs. True when every save landed, or nothing needed
   * one. Reads position and view through the refs: a queued control must
   * flush the screen the walk stands on NOW, not a stale closure's. */
  const flushCurrentScreen = useCallback(async (): Promise<boolean> => {
    const position = positionRef.current;
    const latest = viewRef.current;
    if (position.kind !== "body" || position.screenRef === null || !latest) {
      return true;
    }
    const screen = visibleBodyScreensOf(latest, getValues()).find(
      (candidate) => candidate.ref === position.screenRef,
    );
    if (!screen) return true;
    return flushSaves(screen.fields.filter(fieldVisible).map((f) => f.ref));
  }, [getValues, fieldVisible, flushSaves]);

  const goToScreen = useCallback(
    (
      screenRef: string,
      opts?: { directed?: FormV2DirectedEntry },
    ): Promise<{ ok: boolean; currentScreenRef: string | null }> =>
      runControl(async () => {
        // Recovery owns the screen; a jump now would race its landing.
        if (reloadingRef.current || !viewRef.current) {
          return { ok: false, currentScreenRef: null };
        }
        const standing = positionRef.current;
        const standingRef =
          standing.kind === "body" ? standing.screenRef : null;
        const flushed = await flushCurrentScreen();
        // A vanished response engages the reload mid-flush — it owns the
        // screen from here; any other failure gets the connection banner.
        if (reloadingRef.current || !viewRef.current) {
          return { ok: false, currentScreenRef: null };
        }
        if (!flushed) {
          setProblem(
            "We couldn't save your answers — check your connection and try again.",
          );
          return { ok: false, currentScreenRef: standingRef };
        }
        // The flush may have reshaped the screens — target the fresh list,
        // the same way advance re-anchors.
        const fresh = visibleBodyScreensOf(viewRef.current, getValues());
        const index = fresh.findIndex(
          (candidate) => candidate.ref === screenRef,
        );
        if (index === -1) return { ok: false, currentScreenRef: standingRef };
        setCommittedValues(
          cloneValue(savedValuesRef.current) as Record<string, unknown>,
        );
        setProblem(null);
        positionRef.current = { kind: "body", screenRef };
        setStep({ kind: "body", index, directed: opts?.directed });
        return { ok: true, currentScreenRef: screenRef };
      }),
    [runControl, flushCurrentScreen, getValues],
  );

  const refresh = useCallback(
    (): Promise<{ standing: FormV2StepRef }> =>
      runControl(async () => {
        // Where the walk stands as of positionRef — computed after every
        // exit so the caller learns where the refresh actually landed.
        const standingNow = (): { standing: FormV2StepRef } => {
          const position = positionRef.current;
          return {
            standing:
              position.kind === "body"
                ? { kind: "body", screenRef: position.screenRef ?? undefined }
                : { kind: position.kind },
          };
        };
        const responseId = responseIdRef.current;
        // Mid-recovery the reload already owns the screen and lands fresh.
        if (!responseId || reloadingRef.current) return standingNow();
        // Flush first: the reconcile resets values to server truth, and an
        // unsaved edit would otherwise be dropped with nothing to retry it.
        const flushed = await flushCurrentScreen();
        if (!flushed) {
          // A vanished response engaged the reload mid-flush — that is the
          // absorbed case; a plain failed save rejects for the harness.
          if (reloadingRef.current) return standingNow();
          setProblem(
            "We couldn't save your answers — check your connection and try again.",
          );
          throw new Error(
            "refresh refused: pending answers could not be saved",
          );
        }
        const standing = positionRef.current;
        try {
          const refreshed = await apiClient.fetchResolvedView(responseId);
          ingestView(refreshed);
          const values = answerValues(refreshed);
          const fresh = visibleBodyScreensOf(refreshed, values);
          // External review, standing by: re-place on the fresh view — a
          // re-selection may have grown new screens to walk; settling back
          // on body_complete re-fires the handoff with fresh state either
          // way.
          if (standing.kind === "body_complete" && walkShape.external) {
            const placed = initialStep(refreshed, values, walkShape);
            if (placed.kind === "body") {
              positionRef.current = {
                kind: "body",
                screenRef: fresh[placed.index]?.ref ?? null,
              };
              setStep(placed);
            } else {
              positionRef.current = { kind: "body_complete", screenRef: null };
              setStep({ kind: "body_complete" });
            }
            return standingNow();
          }
          // Only a body step re-anchors; start, review and the end states
          // keep standing where they stand, on the fresh view.
          if (standing.kind !== "body") return standingNow();
          const index =
            standing.screenRef === null
              ? -1
              : fresh.findIndex(
                  (candidate) => candidate.ref === standing.screenRef,
                );
          if (index >= 0) {
            positionRef.current = standing;
            setStep({
              kind: "body",
              index,
              returnToReview: standing.returnToReview,
            });
            return standingNow();
          }
          // The screen refreshed away underneath the customer — land where
          // a reload would, except never backward onto the start screen.
          const fallback = initialStep(refreshed, values, walkShape);
          if (fallback.kind === "body") {
            positionRef.current = {
              kind: "body",
              screenRef: fresh[fallback.index]?.ref ?? null,
            };
            setStep(fallback);
          } else if (fallback.kind !== "start") {
            positionRef.current = { kind: fallback.kind, screenRef: null };
            setStep(fallback);
          } else if (walkShape.external) {
            positionRef.current = { kind: "body_complete", screenRef: null };
            setStep({ kind: "body_complete" });
          } else if (refreshed.definition.show_review) {
            positionRef.current = { kind: "review", screenRef: null };
            setStep({ kind: "review" });
          } else if (fresh.length > 0) {
            positionRef.current = { kind: "body", screenRef: fresh[0].ref };
            setStep({ kind: "body", index: 0 });
          } else {
            positionRef.current = { kind: "start", screenRef: null };
            setStep({ kind: "start" });
          }
          return standingNow();
        } catch (error) {
          // Gone underneath us — recovery owns the screen from here.
          if (recoverIfGone(error)) return standingNow();
          throw error;
        }
      }),
    [
      runControl,
      flushCurrentScreen,
      apiClient,
      ingestView,
      recoverIfGone,
      walkShape,
    ],
  );

  const complete = useCallback(
    (): Promise<{ ok: true } | { ok: false; status: number }> =>
      runControl(async () => {
        const responseId = responseIdRef.current;
        if (!responseId || reloadingRef.current) {
          throw new Error("complete refused: no response is loaded");
        }
        const flushed = await flushCurrentScreen();
        if (!flushed) {
          if (reloadingRef.current) {
            throw new Error(
              "complete refused: the response reloaded mid-flush",
            );
          }
          setProblem(
            "We couldn't save your answers — check your connection and try again.",
          );
          throw new Error(
            "complete refused: pending answers could not be saved",
          );
        }
        setCommittedValues(
          cloneValue(savedValuesRef.current) as Record<string, unknown>,
        );
        emitEvent({ type: "form_submitted" });
        try {
          await apiClient.complete(responseId);
        } catch (error) {
          if (recoverIfGone(error)) return { ok: false, status: 404 };
          if (!isRefusal(error)) throw error;
          emitEvent({
            type: "form_completion_refused",
            status: error.statusCode,
          });
          // Put the refused state on screen — the stale or missing answers
          // the server named — without moving; the caller owns what the
          // walk does next.
          try {
            ingestView(await apiClient.fetchResolvedView(responseId));
          } catch (readError) {
            recoverIfGone(readError);
          }
          return { ok: false, status: error.statusCode };
        }
        acknowledgeCompletion(responseId);
        positionRef.current = { kind: "end", screenRef: null };
        setStep({ kind: "end" });
        return { ok: true };
      }),
    [
      runControl,
      flushCurrentScreen,
      apiClient,
      emitEvent,
      acknowledgeCompletion,
      recoverIfGone,
      ingestView,
    ],
  );

  useImperativeHandle(ref, () => ({ goToScreen, refresh, complete }), [
    goToScreen,
    refresh,
    complete,
  ]);

  if (loadFailed) {
    return (
      <div className="text-center">
        <h1 className="mb-2 font-serif">Sorry, we could not find that form</h1>
        <p>The link may have expired — please reach out to us.</p>
      </div>
    );
  }
  if (!view) {
    if (loadingScreen) {
      return (
        <FormLoadingScreen
          section={loadingScreen.section}
          title={loadingScreen.title}
          description={loadingScreen.description}
        />
      );
    }
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const answerableFields = allVisibleFields.filter(
    (field) => field.type !== "section_heading",
  );
  const staleFieldRefs = new Set(view.progress.stale_field_refs);
  const answeredCount = answerableFields.filter(
    (field) => formData[field.ref] !== undefined && formData[field.ref] !== "",
  ).length;
  const progressPercent =
    step.kind === "end"
      ? 100
      : Math.min(
          99,
          answerableFields.length === 0
            ? 0
            : Math.round((answeredCount / answerableFields.length) * 100),
        );
  const stepKey = step.kind === "body" ? `body-${step.index}` : step.kind;

  return (
    <div className="w-full">
      {view.definition.show_progress && step.kind !== "start" && (
        <ProgressBar progress={progressPercent} />
      )}

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={stepKey}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          // The mockup swaps screens instantly and animates only the
          // incoming one — an exit fade doubles the felt duration.
          exit={{ opacity: 0, transition: { duration: 0 } }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {step.kind === "start" && (
            <ScreenChrome
              section={startScreen?.variant.properties.section}
              title={interpolate(
                startScreen?.variant.title ?? view.definition.title,
              )}
              subtitle={startScreen ? startScreen.variant.subtitle : null}
              description={startScreen?.variant.properties.description}
              footnote={startScreen?.variant.properties.footnote}
              interpolate={interpolate}
              actions={
                <>
                  <div />
                  <Button
                    variant="continue"
                    isLoading={busy}
                    icon={
                      <ChevronRightIcon
                        aria-hidden="true"
                        className="h-4 w-4"
                        strokeWidth={2.5}
                      />
                    }
                    iconPosition="right"
                    onClick={() => void startForm()}
                  >
                    Start
                  </Button>
                </>
              }
            >
              {view.definition.show_review && (
                <p className="italic text-evergreen">
                  You'll have a chance to review all your answers before
                  submitting.
                </p>
              )}
            </ScreenChrome>
          )}

          {step.kind === "body" && bodyScreens[step.index] && (
            <ScreenChrome
              section={bodyScreens[step.index].variant.properties.section}
              title={interpolate(bodyScreens[step.index].variant.title)}
              subtitle={bodyScreens[step.index].variant.subtitle}
              description={
                bodyScreens[step.index].variant.properties.description
              }
              footnote={bodyScreens[step.index].variant.properties.footnote}
              interpolate={interpolate}
              actions={
                readOnly ? undefined : (
                  <>
                    <div>
                      <BodyBackLink
                        step={step}
                        busy={busy}
                        hasPriorWalkScreen={bodyScreens.some(
                          (candidate, index) =>
                            index < step.index &&
                            !walkShape.held.has(candidate.ref),
                        )}
                        onStepBack={goBack}
                        onBackFromFirstScreen={hooks?.onBackFromFirstScreen}
                      />
                    </div>
                    <Button
                      variant="continue"
                      isLoading={busy}
                      icon={
                        <ChevronRightIcon
                          aria-hidden="true"
                          className="h-4 w-4"
                          strokeWidth={2.5}
                        />
                      }
                      iconPosition="right"
                      onClick={() => {
                        const directed = step.directed;
                        if (directed) {
                          void directedContinue(step.index, directed);
                        } else {
                          void advance(step.index, step.returnToReview);
                        }
                      }}
                    >
                      {step.directed?.continueLabel ?? nextButtonLabel(step)}
                    </Button>
                  </>
                )
              }
            >
              <FormProvider {...methods}>
                {/* One switch turns the whole screen into a record: the
                    disabled fieldset takes every NATIVE input and button
                    out of play (display:contents keeps it invisible to
                    layout), and the context reaches the script-driven
                    controls the fieldset cannot — see read-only-context. */}
                <FormReadOnlyContext.Provider value={readOnly}>
                  <fieldset
                    disabled={readOnly}
                    className="contents"
                    aria-label={
                      readOnly ? "Your answers, read-only" : undefined
                    }
                  >
                    <div className="flex flex-col gap-5">
                      <AnimatePresence initial={false}>
                        {groupIntoRows(
                          bodyScreens[step.index].fields.filter(fieldVisible),
                        ).map((row) => (
                          // The space jumps open — no height animation, siblings
                          // move instantly — then the revealed content slides up
                          // and fades in on the mockup's tight timing.
                          <motion.div
                            key={row[0].variant.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, transition: { duration: 0.1 } }}
                            transition={{ duration: 0.28, ease: "easeOut" }}
                          >
                            <FieldRow
                              row={row}
                              interpolate={interpolate}
                              onCommit={commitField}
                            />
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </fieldset>
                </FormReadOnlyContext.Provider>
              </FormProvider>
              {problem && (
                <div className="mt-4">
                  <ProblemBanner>{problem}</ProblemBanner>
                </div>
              )}
            </ScreenChrome>
          )}

          {step.kind === "review" && (
            <ScreenChrome
              title={interpolate(
                view.definition.review_title ?? "Review your answers",
              )}
              subtitle={null}
              description={
                view.definition.review_description ??
                "Check everything over, then submit."
              }
              interpolate={interpolate}
              actions={
                <>
                  <div />
                  <Button
                    variant="continue"
                    isLoading={busy}
                    icon={
                      <ChevronRightIcon
                        aria-hidden="true"
                        className="h-4 w-4"
                        strokeWidth={2.5}
                      />
                    }
                    iconPosition="right"
                    onClick={submit}
                  >
                    Submit
                  </Button>
                </>
              }
            >
              {staleFieldRefs.size > 0 && (
                <div className="mb-4 rounded-xl border border-thistle bg-thistle/15 px-5 py-4 text-base leading-[1.55] text-soil">
                  Some details on your file changed since you started, so a few
                  answers need another look.
                </div>
              )}
              {problem && (
                <div className="mb-4">
                  <ProblemBanner>{problem}</ProblemBanner>
                </div>
              )}
              <ReviewList
                screens={bodyScreens}
                fieldVisible={fieldVisible}
                values={formData}
                needsAnotherLook={staleFieldRefs}
                interpolate={interpolate}
                onEdit={(screenIndex) => void editFromReview(screenIndex)}
              />
            </ScreenChrome>
          )}

          {step.kind === "body_complete" && (
            // The harness heard onBodyComplete and owns what renders next —
            // its juncture faces, its review. This face only shows in the
            // gap before it takes over.
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          )}

          {step.kind === "context_missing" && (
            <ScreenChrome
              title={interpolate(
                view.definition.context_missing_title ??
                  "You're all set for now",
              )}
              subtitle={null}
              description={
                view.definition.context_missing_description ??
                "We have everything we need from you at this step — we'll be in touch to finish the rest together."
              }
              interpolate={interpolate}
            />
          )}

          {step.kind === "end" && (
            <ScreenChrome
              section={endScreen?.variant.properties.section}
              title={interpolate(endScreen?.variant.title ?? "Thank you")}
              subtitle={endScreen ? endScreen.variant.subtitle : null}
              description={endScreen?.variant.properties.description}
              footnote={endScreen?.variant.properties.footnote}
              interpolate={interpolate}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default forwardRef(FormV2);

function answerValues(view: V2ResolvedView): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const answer of view.answers) {
    // A stale answer was given to different wording; it re-asks empty.
    if (answer.stale) continue;
    values[answer.field_ref] = answer.value;
  }
  return values;
}

function cloneValue(value: unknown): unknown {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
}

/** Every rendered field wears its ref, so a refusal can be brought into
 * view whatever the input type renders as. The scroll margin leaves the
 * label a breath of room under the viewport edge. */
const FIELD_ANCHOR_ATTRIBUTE = "data-field-ref";
const FIELD_ANCHOR_CLASS = "scroll-mt-6";

function fieldAnchor(field: V2ResolvedField): Record<string, string> {
  return { [FIELD_ANCHOR_ATTRIBUTE]: field.ref };
}

/**
 * Glide — not snap — to a field the validation refused, when it is not
 * already on screen. A required input scrolled off the top of a phone
 * otherwise fails in silence: the button says nothing and the red line
 * sits out of sight. Readers who asked for no motion get the jump.
 */
function revealField(fieldRef: string | undefined): void {
  if (!fieldRef) return;
  const anchor = document.querySelector<HTMLElement>(
    `[${FIELD_ANCHOR_ATTRIBUTE}="${CSS.escape(fieldRef)}"]`,
  );
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  anchor.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  });
}

/**
 * Snap — not glide — to the top. A refused submit surfaces its explanation
 * up there, and on a phone the Submit button is a full screen below it;
 * the instant jump is what makes the banner impossible to miss.
 */
function snapToTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}

/** What went wrong, worn like the stale-answers notice — a dialog, not a stray line. */
function ProblemBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium leading-[1.55] text-red-800">
      {children}
    </div>
  );
}

/** How external review reshapes the walk: the screens the natural walk
 * skips, and whether review/completion belong to the harness. The
 * standalone renderer walks with an empty shape. */
interface WalkShape {
  held: ReadonlySet<string>;
  external: boolean;
}

const STANDALONE_WALK: WalkShape = { held: new Set(), external: false };

/**
 * Where a reload lands: completed responses on the end screen; in-progress
 * ones on the first screen still owing a required answer (stale re-asks
 * included) — but never BEYOND the last screen the user actually touched:
 * a fully-answered stretch must not advance a reload onto a screen the
 * user has never seen, where they cannot tell whether they skipped
 * something. "Touched" is an answer stamped meaningfully after the walk
 * began (creation-time prefill shares the start stamp, so it never
 * counts). Otherwise review when the form has one; a fresh response at
 * the start. Held screens neither receive the landing nor count toward
 * "unfinished" — what the harness holds is the harness's to direct — and
 * a walk done except for them lands on body_complete.
 */
function initialStep(
  view: V2ResolvedView,
  values: Record<string, unknown>,
  shape: WalkShape = STANDALONE_WALK,
): Step {
  if (view.form_response.status === "completed") return { kind: "end" };
  if (Object.keys(values).length === 0) return { kind: "start" };

  const scope: ConditionScope = {
    answers: values,
    context: view.form_response.resolved_context,
    variables: view.variables,
  };
  const visibleBodyScreens = visibleBodyScreensOf(view, values);
  if (visibleBodyScreens.length === 0) return { kind: "start" };

  const needsWork = new Set([
    ...view.progress.unanswered_required_field_refs,
    ...view.progress.stale_field_refs,
  ]);
  const firstUnfinished = visibleBodyScreens.findIndex(
    (screen) =>
      !shape.held.has(screen.ref) &&
      screen.fields.some(
        (field) =>
          needsWork.has(field.ref) &&
          (field.variant.visible_when === null ||
            evaluateCondition(field.variant.visible_when, scope)),
      ),
  );
  if (firstUnfinished >= 0) {
    const touched = lastTouchedIndex(view, visibleBodyScreens, shape);
    const index =
      touched >= 0 && firstUnfinished > touched ? touched : firstUnfinished;
    return { kind: "body", index };
  }
  if (shape.external) return { kind: "body_complete" };
  if (view.definition.show_review) return { kind: "review" };
  return { kind: "body", index: visibleBodyScreens.length - 1 };
}

/** The prefill margin, mirroring the server's post-start rule: prefill is
 * written in the creation transaction and shares its timestamp, so this
 * only has to absorb clock jitter. */
const TOUCH_MARGIN_MS = 5_000;

/**
 * The index of the last walk screen holding an answer the user gave —
 * one stamped after the walk began — or -1 when only prefill exists.
 * Falls back to the earliest answer as the baseline when an older API
 * build omits started_at.
 */
function lastTouchedIndex(
  view: V2ResolvedView,
  visibleBodyScreens: V2ResolvedScreen[],
  shape: WalkShape,
): number {
  const stamps = view.answers.map((answer) => Date.parse(answer.answered_at));
  const started = view.form_response.started_at
    ? Date.parse(view.form_response.started_at)
    : Math.min(...stamps);
  const baseline = started + TOUCH_MARGIN_MS;
  const touchedRefs = new Set(
    view.answers
      .filter((answer) => Date.parse(answer.answered_at) > baseline)
      .map((answer) => answer.field_ref),
  );
  if (touchedRefs.size === 0) return -1;
  for (let index = visibleBodyScreens.length - 1; index >= 0; index -= 1) {
    const screen = visibleBodyScreens[index];
    if (shape.held.has(screen.ref)) continue;
    if (screen.fields.some((field) => touchedRefs.has(field.ref))) {
      return index;
    }
  }
  return -1;
}

/**
 * Where a walk that never shows a start screen begins: the first body
 * screen the walk may enter, or straight to review (the harness's
 * body_complete) when there is nothing to ask. A form with no body
 * screens and no review keeps its start screen after all — its Start
 * button is the only way to submit it.
 */
function stepPastStart(
  view: V2ResolvedView,
  values: Record<string, unknown>,
  shape: WalkShape = STANDALONE_WALK,
): Step {
  const visible = visibleBodyScreensOf(view, values);
  const firstWalkIndex = visible.findIndex(
    (screen) => !shape.held.has(screen.ref),
  );
  if (firstWalkIndex >= 0) return { kind: "body", index: firstWalkIndex };
  if (shape.external) return { kind: "body_complete" };
  if (view.definition.show_review) return { kind: "review" };
  return { kind: "start" };
}

/** The screens the natural walk covers: visible body screens outside the
 * held set — what onBodyComplete hands the harness. */
function walkScreensOf(
  view: V2ResolvedView,
  answers: Record<string, unknown>,
  held: ReadonlySet<string>,
): V2ResolvedScreen[] {
  return visibleBodyScreensOf(view, answers).filter(
    (screen) => !held.has(screen.ref),
  );
}

/**
 * The required parts a composite answer must carry to count — the
 * client-side mirror of the server's completeness rule, kept in sync by
 * hand with COMPOSITE_REQUIRED_PARTS in after-internal
 * src/server/modules/forms/domain/helpers/response-progress.ts.
 */
const COMPOSITE_REQUIRED_PARTS: Record<string, readonly string[]> = {
  full_name: ["first_name", "last_name"],
  location: ["address_line_1", "city", "state", "zip"],
};

function answerComplete(type: string, value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  const requiredParts = COMPOSITE_REQUIRED_PARTS[type];
  if (requiredParts === undefined) return true;
  if (typeof value !== "object") return false;
  const parts = value as Record<string, unknown>;
  return requiredParts.every((part) => {
    const partValue = parts[part];
    if (typeof partValue === "string") return partValue.trim() !== "";
    return partValue !== undefined && partValue !== null;
  });
}

/**
 * Where a just-saved field stands: the visible body screen serving it,
 * whether it is required there, and whether that screen's whole required
 * set now holds complete saved answers — the enrichment `onFieldSaved`
 * carries for the harness's checkpoint prefetch.
 */
function savedFieldContext(
  view: V2ResolvedView | null,
  answers: Record<string, unknown>,
  fieldRef: string,
  savedValues: Record<string, unknown>,
): {
  screenRef: string | null;
  required: boolean;
  screenRequiredComplete: boolean;
} {
  if (!view) {
    return { screenRef: null, required: false, screenRequiredComplete: false };
  }
  const scope: ConditionScope = {
    answers,
    context: view.form_response.resolved_context,
    variables: view.variables,
  };
  const visible = (field: V2ResolvedField): boolean =>
    field.variant.visible_when === null ||
    evaluateCondition(field.variant.visible_when, scope);

  for (const screen of visibleBodyScreensOf(view, answers)) {
    const field = screen.fields.find(
      (candidate) => candidate.ref === fieldRef && visible(candidate),
    );
    if (!field) continue;
    const requiredFields = screen.fields.filter(
      (candidate) =>
        visible(candidate) && candidate.variant.validations.required === true,
    );
    return {
      screenRef: screen.ref,
      required: field.variant.validations.required === true,
      screenRequiredComplete: requiredFields.every((candidate) =>
        answerComplete(candidate.type, savedValues[candidate.ref]),
      ),
    };
  }
  return { screenRef: null, required: false, screenRequiredComplete: false };
}

/** The body screens a view serves under these answers — the same walk the
 * `bodyScreens` memo renders, computable off a just-arrived view before
 * React has committed it. */
function visibleBodyScreensOf(
  view: V2ResolvedView,
  answers: Record<string, unknown>,
): V2ResolvedScreen[] {
  const scope: ConditionScope = {
    answers,
    context: view.form_response.resolved_context,
    variables: view.variables,
  };
  return view.screens.filter(
    (screen) =>
      screen.kind === "body" &&
      (screen.visible_when === null ||
        evaluateCondition(screen.visible_when, scope)),
  );
}

/** The field this client holds for a ref — hidden fields included, their
 * wording rides the screens payload for the client-side reveal. */
function servedField(
  view: V2ResolvedView | null,
  fieldRef: string,
): V2ResolvedField | null {
  if (!view) return null;
  for (const screen of view.screens) {
    for (const field of screen.fields) {
      if (field.ref === fieldRef) return field;
    }
  }
  return null;
}

/** The step kinds analytics ever names as a "screen". `body_complete` and
 * `context_missing` are harness handoffs the customer never sees as a
 * distinct screen, so events keyed on them are skipped rather than forced
 * into this vocabulary. */
function formScreenKind(kind: Step["kind"]): FormV2ScreenKind | null {
  switch (kind) {
    case "start":
    case "body":
    case "review":
    case "end":
      return kind;
    default:
      return null;
  }
}

/** Which resolved screen a mapped kind points at — "review" has none of
 * its own (the renderer's own step, not something the server resolved). */
function screenFor(
  kind: FormV2ScreenKind,
  startScreen: V2ResolvedScreen | null,
  bodyScreens: V2ResolvedScreen[],
  bodyIndex: number | null,
  endScreen: V2ResolvedScreen | null,
): V2ResolvedScreen | null {
  if (kind === "start") return startScreen;
  if (kind === "end") return endScreen;
  if (kind === "body") {
    return bodyIndex === null ? null : (bodyScreens[bodyIndex] ?? null);
  }
  return null;
}

/** The refs among `fields` that `trigger()` just refused — the top-level
 * ref itself, or (composite types register per part) any part under it. */
/** The visible fields `trigger()` just failed. Read through
 * `getFieldState`, which consults the form's live state — the `formState`
 * proxy only reflects fields a render has subscribed to, and a callback
 * asking right after validation has subscribed to none. */
function invalidFieldRefs(
  fields: V2ResolvedField[],
  methods: Pick<UseFormReturn, "getFieldState">,
): string[] {
  return fields
    .filter((field) =>
      validationNames(field).some(
        (name) => methods.getFieldState(name).invalid,
      ),
    )
    .map((field) => field.ref);
}

/**
 * The react-hook-form names that carry a field's validation rules.
 * Composite fields register per part, and trigger() on the parent path
 * does not cascade into them — so name the parts explicitly.
 */
function validationNames(field: V2ResolvedField): string[] {
  if (field.type === "full_name") {
    return ["first_name", "middle_name", "last_name"].map(
      (part) => `${field.ref}.${part}`,
    );
  }
  if (field.type === "location") {
    return ["address_line_1", "address_line_2", "city", "state", "zip"].map(
      (part) => `${field.ref}.${part}`,
    );
  }
  return [field.ref];
}

/**
 * Consecutive fields sharing a layout_row render side by side; composite
 * types always stand alone.
 */
function groupIntoRows(fields: V2ResolvedField[]): V2ResolvedField[][] {
  const rows: V2ResolvedField[][] = [];
  let openRow: V2ResolvedField[] | null = null;
  let openRowKey: number | null = null;
  for (const field of fields) {
    const standsAlone =
      field.layout_row === null || FULL_ROW_TYPES.has(field.type);
    if (standsAlone) {
      rows.push([field]);
      openRow = null;
      openRowKey = null;
      continue;
    }
    if (openRow && field.layout_row === openRowKey) {
      openRow.push(field);
      continue;
    }
    openRow = [field];
    openRowKey = field.layout_row;
    rows.push(openRow);
  }
  return rows;
}

function toRenderable(
  field: V2ResolvedField,
  interpolate: (input: string) => string,
): RenderableField {
  const properties = field.variant.field_properties;
  return {
    ref: field.ref,
    type: field.type,
    title: interpolate(field.variant.title),
    description: properties.description
      ? interpolate(properties.description)
      : undefined,
    placeholder: properties.placeholder,
    choices: properties.choices,
    allowMultiple: properties.allow_multiple_selection,
    required: field.variant.validations.required ?? false,
    minLength: field.variant.validations.min_length,
    maxLength: field.variant.validations.max_length,
    pattern: field.variant.validations.pattern,
    patternMessage: field.variant.validations.pattern_message,
    sensitive: field.sensitive,
  };
}

/** The v1 shape, for the field types that still fall back to v1 rendering. */
function toFieldConfig(
  field: V2ResolvedField,
  interpolate: (input: string) => string,
): FieldConfig {
  return {
    ref: field.ref,
    type: field.type as FieldConfig["type"],
    title: interpolate(field.variant.title),
    properties: {
      ...field.variant.field_properties,
      description: field.variant.field_properties.description
        ? interpolate(field.variant.field_properties.description)
        : undefined,
      choices: field.variant.field_properties.choices?.map((choice) => ({
        ref: choice.ref,
        label: choice.label,
      })),
    },
    validations: field.variant.validations,
  };
}

interface FieldRowProps {
  row: V2ResolvedField[];
  interpolate: (input: string) => string;
  onCommit: (fieldRef: string) => void;
}

function FieldRow({ row, interpolate, onCommit }: FieldRowProps) {
  if (row.length === 1) {
    const field = row[0];
    return (
      <div {...fieldAnchor(field)} className={FIELD_ANCHOR_CLASS}>
        <V2Field
          field={toRenderable(field, interpolate)}
          fallbackConfig={toFieldConfig(field, interpolate)}
          onCommit={() => onCommit(field.ref)}
        />
      </div>
    );
  }
  // Side by side from 360px up, single column only on the very smallest
  // screens. The cells share label/input/helper/error row tracks via
  // subgrid, so the inputs stay in line even when one label wraps to two
  // lines and its neighbor's doesn't; items-end keeps each label hugging
  // its own input inside the shared label track. From sm, layout_width
  // becomes the column's fr weight.
  const columns = row.map((field) => `${field.layout_width ?? 1}fr`).join(" ");
  return (
    <div
      className="grid grid-cols-1 gap-3 2xs:grid-cols-2 2xs:grid-rows-[auto_auto_auto_auto] 2xs:gap-y-0 sm:grid-cols-[var(--row-columns)]"
      style={{ "--row-columns": columns } as React.CSSProperties}
    >
      {row.map((field) => (
        <div
          key={field.variant.id}
          {...fieldAnchor(field)}
          // With the field's root flattened to display:contents, its
          // children are the real grid items — they need the min-w-0, or a
          // long select value refuses to shrink and overflows its column.
          className={`${FIELD_ANCHOR_CLASS} min-w-0 2xs:row-span-4 2xs:grid 2xs:grid-rows-subgrid 2xs:items-end 2xs:[&>div>*]:min-w-0 2xs:[&>div]:contents`}
        >
          <V2Field
            field={toRenderable(field, interpolate)}
            fallbackConfig={toFieldConfig(field, interpolate)}
            onCommit={() => onCommit(field.ref)}
          />
        </div>
      ))}
    </div>
  );
}

/** The walk's forward button always says Continue — the screens can grow
 * mid-form, so "last" is not a promise worth labeling. Editing from
 * review keeps its own label; that path is a round trip, not the walk. */
function nextButtonLabel(step: { returnToReview?: boolean }): string {
  return step.returnToReview ? "Save & return to review" : "Continue";
}

interface BodyBackLinkProps {
  step: {
    index: number;
    returnToReview?: boolean;
    directed?: FormV2DirectedEntry;
  };
  busy: boolean;
  /** Whether the natural walk has anywhere earlier to step back to. */
  hasPriorWalkScreen: boolean;
  onStepBack: (fromIndex: number) => Promise<void>;
  onBackFromFirstScreen?: () => void;
}

/** The Back link a body screen wears: the directed stay's hand-back when
 * its harness offered one; a step back mid-walk; the harness's exit on
 * the first walk screen, when it offered one; nothing otherwise. A
 * review round trip shows no Back — Save & return is the only way out. */
function BodyBackLink({
  step,
  busy,
  hasPriorWalkScreen,
  onStepBack,
  onBackFromFirstScreen,
}: BodyBackLinkProps) {
  if (step.directed) {
    const handBack = step.directed.onBack;
    if (!handBack) return null;
    return (
      <button
        type="button"
        className={BACK_LINK_CLASS}
        disabled={busy}
        onClick={handBack}
      >
        Back
      </button>
    );
  }
  if (step.returnToReview) return null;
  const onBack = hasPriorWalkScreen
    ? () => void onStepBack(step.index)
    : onBackFromFirstScreen;
  if (!onBack) return null;
  return (
    <button
      type="button"
      className={BACK_LINK_CLASS}
      disabled={busy}
      onClick={onBack}
    >
      Back
    </button>
  );
}

/** The walk's named loading state: the walk's own screen chrome over the
 * house spinner, so it renders exactly like any other screen of the form.
 * Exported so a host can hold the SAME pixels while its own data loads
 * and hand off to the walk's loader without anything moving — hosts pass
 * a rotating status line through `description` for longer waits. */
export function FormLoadingScreen({
  section,
  title,
  description,
}: {
  section?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
}) {
  return (
    <ScreenChrome
      section={section}
      title={title}
      subtitle={null}
      description={undefined}
      interpolate={verbatim}
    >
      <div className="flex flex-col items-center gap-6 py-14">
        <Spinner />
        {description !== undefined && (
          <div className="text-lg text-oat-700">{description}</div>
        )}
      </div>
    </ScreenChrome>
  );
}

/** A loading screen has no committed answers to interpolate from. */
function verbatim(input: string): string {
  return input;
}

interface ScreenChromeProps {
  section?: string;
  title: React.ReactNode;
  subtitle: string | null;
  description: string | undefined;
  footnote?: { icon?: string; text: string };
  interpolate: (input: string) => string;
  children?: React.ReactNode;
  /** The Back/Continue row — the footnote sits ABOVE it, per the mockup. */
  actions?: React.ReactNode;
}

function ScreenChrome({
  section,
  title,
  subtitle,
  description,
  footnote,
  interpolate,
  children,
  actions,
}: ScreenChromeProps) {
  return (
    <div className="w-full">
      {section && (
        <p className="mb-2.5 text-[13px] font-bold uppercase tracking-[0.25em] text-evergreen">
          {interpolate(section)}
        </p>
      )}
      <h1 className="mb-3 font-serif text-[31px] font-normal leading-[1.15] text-soil sm:text-[39px]">
        {title}
      </h1>
      {subtitle && (
        <p className="mb-2 text-lg text-soil">{interpolate(subtitle)}</p>
      )}
      {description && (
        <p className="mb-8 max-w-[56ch] text-xl leading-[1.55] text-oat-700">
          {interpolate(description)}
        </p>
      )}
      {children}
      {footnote && <Footnote footnote={footnote} interpolate={interpolate} />}
      {actions && (
        <div className="mt-8 flex items-center justify-between">{actions}</div>
      )}
    </div>
  );
}

function Footnote({
  footnote,
  interpolate,
}: {
  footnote: { icon?: string; text: string };
  interpolate: (input: string) => string;
}) {
  const Icon = footnote.icon ? FOOTNOTE_ICONS[footnote.icon] : undefined;
  return (
    <div className="mt-6 flex items-center gap-3.5 rounded-xl bg-oat px-5 py-[18px]">
      {Icon && (
        <Icon
          aria-hidden="true"
          strokeWidth={2}
          className="h-6 w-6 shrink-0 text-evergreen"
        />
      )}
      <p className="text-base leading-[1.55] text-oat-700">
        {interpolate(footnote.text)}
      </p>
    </div>
  );
}
